import React, { useEffect, useState } from "react";
import moment from "moment";

// External components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataBreachRiskIndicator from "components/DataBreachRiskIndicator";
import { getDataBreachRiskStatus } from "utilities/getDataBreachRiskStatus";

// Translation
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";

// APIs
import { useMaterialUIController } from "context";
import dataBreachManaged from "api/dataBreach/dataBreachManaged";
import { getSingleDataBreachById } from "api/dataBreach/getSingleDataBreach";
import { getSingleDataBreachByDarkWebThreatId } from "api/dataBreach/getSingleDataBreach";

const DataBreachDetails = () => {
  const [data, setData] = useState(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const [controller] = useMaterialUIController();
  const { user } = controller;

  const getData = async () => {
    try {
      const response = await getSingleDataBreachById(id);

      let mergedData = { ...response }; // parte dai dati della prima chiamata

      if (response?.darkwebThreatId) {
        try {
          const darkWebResponse = await getSingleDataBreachByDarkWebThreatId(
            response.darkwebThreatId
          );
          // Uniamo i dati della seconda chiamata con quelli della prima
          // Le rows originali rimangono
          mergedData = {
            ...darkWebResponse,
            results: response.results, // manteniamo le rows originali
          };
        } catch (err) {
          console.error("Error fetching by darkwebThreatId:", err);
          // Se fallisce la seconda chiamata, manteniamo solo i dati della prima
          mergedData = { ...response };
        }
      }

      setData(mergedData);
    } catch (e) {
      console.error("error --->", e);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  function getResults() {
    if (!data?.results || !Array.isArray(data.results)) return null;

    return data.results.map((res, index) => {
      if (res.rows && res.rows.length > 0) {
        return res.rows.map((row, indexRow) => (
          <div
            key={`data-breach-result-${index.toString()}-${indexRow.toString()}`}
            className="boxes-border-radius"
            style={{ backgroundColor: "#fff", padding: "15px", marginBottom: "30px" }}
          >
            <div className="data-breach-details-intro-line">#{(indexRow + 1).toString()}</div>
            {Object.keys(row).map((element) => {
              if (element && element.length > 1 && row[element]) {
                return (
                  <div
                    key={`data-breach-result-${index.toString()}-${indexRow.toString()}-${element}`}
                    style={{ fontSize: "16px" }}
                  >
                    <span>{t(`dataBreach.labels.${element}`)}:</span>{" "}
                    <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                      {Array.isArray(row[element]) ? row[element].join("/") : row[element]}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        ));
      }
      return null;
    });
  }

  function getNumberOfAttacks() {
    let attacksNumber = 0;

    if (Array.isArray(data?.results)) {
      data.results.forEach((res) => {
        if (Array.isArray(res.rows)) {
          attacksNumber += res.rows.length;
        }
      });
    }

    return `(${attacksNumber})`;
  }

  function getDataBreachRiskLabel(riskLevel, columns) {
    if (!columns || !Array.isArray(columns)) {
      return "Unknown risk level";
    }
    switch (riskLevel.status) {
      case "high":
        return t("dataBreach.risk_level_label_high");
      case "medium":
        return t("dataBreach.risk_level_label_medium");
      case "low":
        return t("dataBreach.risk_level_label_low");
      default:
        return "Unknown risk level";
    }
  }

  function getResultsTables() {
    if (!data?.results || !Array.isArray(data.results)) return null;

    return data.results.map((res, index) => {
      if (!res.rows || res.rows.length === 0) return null;

      // Prendi le colonne dinamicamente dalla prima row
      const columns = Object.keys(res.rows[0]);

      return (
        <div
          key={`data-breach-table-${index}`}
          className="boxes-border-radius"
          style={{ backgroundColor: "#fff", padding: "15px", marginBottom: "30px" }}
        >
          <h3 style={{ marginBottom: "15px" }}>
            {t("dataBreach.client_label")}: {res.client} ({res.rows.length}{" "}
            {t("dataBreach.records")})
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={`header-${col}`}
                    style={{
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                      padding: "8px",
                      fontWeight: "600",
                    }}
                  >
                    {t(`dataBreach.labels.${col}`) || col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {res.rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {columns.map((col) => (
                    <td key={`row-${rowIndex}-col-${col}`} style={{ padding: "8px" }}>
                      {Array.isArray(row[col]) ? row[col].join("/") : row[col]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* back to the results */}
      <div style={{ marginBottom: "15px" }}>
        <Link className="back-to-results-link" to="/databreaches/">
          {t("dataBreach.back_to_results")}
        </Link>
      </div>
      {/* Title */}

      <h2 className="page-title">{data && data.shortDescription}</h2>

      {/* Box */}
      <div className="boxes-border-radius" style={{ background: "white", padding: "15px" }}>
        {data ? (
          <>
            <dl style={{ fontSize: "16px" }}>
              <dt style={{ fontWeight: "600" }}>{t("dataBreach.name")}</dt>
              <dd style={{ marginBottom: "15px" }}>{data.threatActor?.name || "N/A"}</dd>

              <dt style={{ fontWeight: "600" }}>{t("dataBreach.incident_date")}</dt>
              <dd style={{ marginBottom: "15px" }}>
                {moment(data.incidentDate).format("DD/MM/YYYY")}
              </dd>

              <dt style={{ fontWeight: "600" }}>{t("dataBreach.published_date")}</dt>
              <dd style={{ marginBottom: "15px" }}>
                {moment(data.publishedDate).format("DD/MM/YYYY")}
              </dd>

              <dt style={{ fontWeight: "600" }}>{t("dataBreach.description")}</dt>
              <dd style={{ marginBottom: "15px" }}>{data.description}</dd>

              <dt style={{ fontWeight: "600" }}>{t("dataBreach.intelligence")}</dt>
              <dd style={{ marginBottom: "15px" }}>{data.intelligence}</dd>

              <dt style={{ fontWeight: "600" }}>{t("dataBreach.columns")}</dt>
              <dd style={{ marginBottom: "15px", display: "flex", gap: "5px" }}>
                {Array.isArray(data.columns) && data.columns.length > 0
                  ? data.columns.map((item, j) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={j}
                        style={{
                          fontSize: "14px",
                          background: "lightgray",
                          borderRadius: "35px",
                          textAlign: "center",
                          marginBottom: "3px",
                          padding: "1px 5px",
                        }}
                      >
                        {t(`dataBreach.labels.${item}`)}
                      </div>
                    ))
                  : null}
              </dd>
              <dt style={{ fontWeight: "600" }}>{t("dataBreach.risk_level")}</dt>
              <dd style={{ marginBottom: "15px" }}>
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    <DataBreachRiskIndicator
                      columns={Array.isArray(data.columns) ? data.columns : []}
                      showTooltip={false}
                    />
                  </div>
                  <div>
                    <p style={{ margin: "0" }}>
                      {getDataBreachRiskLabel(getDataBreachRiskStatus(data.columns), data.columns)}
                    </p>
                  </div>
                </div>
              </dd>
              <dt style={{ fontWeight: "600" }}>{t("dataBreach.whatHappened_title")}</dt>
              <dd style={{ marginBottom: "15px" }}>{t("dataBreach.whatHappened_body")}</dd>

              {user && user.role && user.role !== "admin" ? (
                <div style={{}}>
                  {!data.managedBy ||
                  (data.managedBy && data.managedBy.length < 1) ||
                  (data.managedBy &&
                    data.managedBy.length > 0 &&
                    user &&
                    user.id &&
                    !data.managedBy.includes(user.id)) ? (
                    <button
                      type="button"
                      className="request-report-button"
                      onClick={() => {
                        if (data.id) {
                          dataBreachManaged(data.id)
                            .then((resManaged) => {
                              if (
                                resManaged &&
                                resManaged.managedBy &&
                                resManaged.managedBy.length > 0
                              ) {
                                const newBreach = { ...data, managedBy: resManaged.managedBy };
                                setData(newBreach);
                              }
                            })
                            .catch((errManaged) => {
                              // eslint-disable-next-line
                              console.log("error setting databreach managed", errManaged);
                            });
                        }
                      }}
                    >
                      {t("dataBreach.manage")}
                    </button>
                  ) : (
                    <div
                      style={{
                        padding: "10px",
                        border: "1px solid green",
                        background: "#e6ffde",
                        color: "#399918",
                        borderRadius: "12px",
                        fontWeight: "600",
                      }}
                    >
                      <span>{t("dataBreach.managed_breach")}</span>
                    </div>
                  )}
                </div>
              ) : null}
            </dl>
          </>
        ) : null}
      </div>

      {data && data.results && data.results.length > 0 ? (
        <div>
          <div className="data-breach-attacks-intro">
            {t("dataBreach.attacks_found")} {getNumberOfAttacks()}
          </div>
          {getResults()}
        </div>
      ) : null}
    </DashboardLayout>
  );
};

export default DataBreachDetails;
